import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  EventEmitter,
  inject,
  Input,
  input,
  Output,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { ModalController, PopoverController } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { CommunicationService, Device, UbieState } from '@ubie/core';
import { NetworkDeviceStateEvent } from '@ubie/mqttcommunication';
import { DevicesByInstallationsDeviceSelected } from '../../../interfaces/devices-by-installation-device-selected.interface';
import { ListItem } from '../../../interfaces/list-item.interface';
import { GeneralPopoverComponent } from '../../general/general-popover/general-popover.component';
import { ActivationModalComponent } from '../activation-modal/activation-modal.component';

@Component({
  selector: 'acl-devices-by-installations-list',
  standalone: false,
  templateUrl: './devices-by-installations-list.component.html',
  styleUrl: './devices-by-installations-list.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DevicesByInstallationsListComponent {
  private readonly router = inject(Router);
  private readonly communicationService = inject(CommunicationService);
  private readonly modalController: ModalController = inject(ModalController);
  private readonly translate = inject(TranslateService);
  private readonly popoverController = inject(PopoverController);
  private readonly destroyRef = inject(DestroyRef);

  private readonly watchedUbieSerials = new Set<string>();
  private readonly watchedNetworkDevices = new Set<string>();
  private lastInstallationId: number | null = null;

  readonly isDisabled = input<boolean>(false);
  readonly isCtdLimitReached = input<boolean>(false);
  readonly installationId = input<number>(0);

  // Received list of devices
  readonly devices = signal<Device[]>([]);
  @Input()
  set data(value: Device[]) {
    this.handleNewDeviceList(value);
  }
  get data(): Device[] {
    return this.devices();
  }

  @Output() readonly deviceSelected =
    new EventEmitter<DevicesByInstallationsDeviceSelected>();

  onClickItem(id: number): void {
    this.router.navigate([
      '/installation/' + this.installationId() + '/device/' + id,
    ]);
  }

  presentPopover(ev: Event, device: Device): void {
    this.deviceSelected.emit({ event: ev, selectedDevice: device });
  }

  handleNewDeviceList(devices: Device[]): void {
    const currentInstallationId = this.installationId();
    if (
      this.lastInstallationId === null ||
      this.lastInstallationId !== currentInstallationId
    ) {
      this.watchedUbieSerials.clear();
      this.watchedNetworkDevices.clear();
      this.lastInstallationId = currentInstallationId;
    }

    const normalizedDevices = devices.map((parent) => ({
      ...parent,
      networkDevices: parent.networkDevices?.map((child) => ({
        ...child,
        device: { serialNumber: parent.serialNumber },
      })),
    }));

    this.devices.set(normalizedDevices);

    const parentSerials = normalizedDevices
      .map((device) => device.serialNumber)
      .filter((serialNumber) => Boolean(serialNumber));

    // Monitor parent ubie status
    parentSerials.forEach((serial) => {
      if (this.watchedUbieSerials.has(serial)) return;
      this.watchedUbieSerials.add(serial);

      this.communicationService
        .getUbieState$(serial)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((state: UbieState) => {
          this.devices.update((currentDevices) =>
            currentDevices.map((device) =>
              device.serialNumber === serial
                ? { ...device, online: state.isConnected }
                : device,
            ),
          );
        });
    });

    // Monitor child network devices
    normalizedDevices.forEach((parent) => {
      parent.networkDevices?.forEach((child) => {
        const key = `${parent.serialNumber}::${child.serialNumber}`;
        if (this.watchedNetworkDevices.has(key)) return;
        this.watchedNetworkDevices.add(key);

        this.communicationService
          .getNetworkDeviceState$(parent.serialNumber, child.serialNumber)
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe((state: NetworkDeviceStateEvent) => {
            this.devices.update((currentDevices) =>
              currentDevices.map((device) => {
                if (device.serialNumber !== parent.serialNumber) return device;

                return {
                  ...device,
                  networkDevices: device.networkDevices?.map((nd) =>
                    nd.serialNumber === child.serialNumber
                      ? {
                          ...nd,
                          online: state.connected,
                          connectable: state.connectable,
                        }
                      : nd,
                  ),
                };
              }),
            );
          });
      });
    });
  }

  /////////////////////////////////////////////////////////
  //#region ACTIVATE CTD
  /////////////////////////////////////////////////////////

  onPresentActivatePopoverClicked(
    ev: Event,
    device: Device,
    networkDevice: Device,
  ): void {
    this.presentActivatePopover(ev, device, networkDevice);
  }

  /**
   * Show popover with activation option for ctd
   * @param ev
   */
  async presentActivatePopover(
    ev: Event,
    device: Device,
    networkDevice: Device,
  ): Promise<void> {
    const dropdownMenu: ListItem[] = [
      {
        name: this.translate.instant('device_activate'),
        click: (): void => {
          this.openActivateModal(device, networkDevice);
          activatePopover.dismiss();
        },
      },
    ];

    const activatePopover = await this.popoverController.create({
      component: GeneralPopoverComponent,
      cssClass: 'general-popover',
      componentProps: { items: dropdownMenu },
      event: ev,
    });

    await activatePopover.present();
  }

  // modal to ask confirmation for activation
  async openActivateModal(
    device: Device,
    networkDevice: Device,
  ): Promise<void> {
    const modal = await this.modalController.create({
      component: ActivationModalComponent,
      componentProps: {
        title: 'device_activateTitle',
        message: 'device_activateMessage',
        activationDevice: {
          networkDeviceSerial: networkDevice.serialNumber,
          ubieSerial: device.serialNumber,
          networkDeviceId: networkDevice.id,
          verificationKey: networkDevice.verificationKey,
        },
      },
    });

    await modal.present();

    const result = await modal.onDidDismiss();
    if (result.data === 'reload') {
      this.devices.update((currentDevices) =>
        currentDevices.map((parent) =>
          parent.serialNumber === device.serialNumber
            ? {
                ...parent,
                networkDevices: parent.networkDevices?.map((child) =>
                  child.serialNumber === networkDevice.serialNumber
                    ? {
                        ...child,
                        connectable: true,
                      }
                    : child,
                ),
              }
            : parent,
        ),
      );
    }
  }

  //#endregion
  /////////////////////////////////////////////////////////
}
